import { motion } from 'framer-motion';
import { Play, Image as ImageIcon } from 'lucide-react';
import type { PortfolioMediaTile } from '../../lib/portfolio-tiles';
import { getMediaAlt } from '../../lib/portfolio-tiles';
import type { CategoryAccent } from '../../lib/category-theme';

type Props = {
  tile: PortfolioMediaTile;
  imageUrl: string;
  accent: CategoryAccent;
  onOpen: () => void;
  compact?: boolean;
};

export default function PortfolioMediaCard({ tile, imageUrl, accent, onOpen, compact }: Props) {
  const isVideo = tile.kind === 'video';
  const alt = getMediaAlt(tile);

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      aria-label={isVideo ? 'Lire la vidéo' : 'Voir la photo'}
      className={`group relative shrink-0 snap-start overflow-hidden rounded-xl bg-zinc-900 ring-1 ring-white/5 transition focus:outline-none focus-visible:ring-2 ${accent.ring} ${
        compact ? 'w-[72vw] sm:w-[280px]' : 'w-[78vw] sm:w-[300px] md:w-[320px]'
      }`}
      whileHover={{ scale: 1.04, y: -4 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={imageUrl}
          alt={alt}
          draggable={false}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          onContextMenu={(e) => e.preventDefault()}
        />
        <div className="absolute inset-0 bg-black/15 transition-colors group-hover:bg-black/35" />

        <div className="absolute inset-0 flex items-center justify-center opacity-80 transition-opacity group-hover:opacity-100">
          <div
            className={`flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-black/35 text-white backdrop-blur-sm ${accent.iconBg}`}
          >
            {isVideo ? (
              <Play className="ml-0.5 h-5 w-5 fill-white" />
            ) : (
              <ImageIcon className="h-5 w-5" />
            )}
          </div>
        </div>

        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    </motion.button>
  );
}
